import { useEffect, useRef, useState } from 'react';
import { send } from '@/shared/messages';
import type { Test, Chapter, Source, Summary, QaMessage } from '@/shared/types';
import { SummaryPanel } from '../components/SummaryPanel';
import { QAChat } from '../components/QAChat';
import { SourcesList } from '../components/SourcesList';
import { GenerateActions } from '../components/GenerateActions';
import { ChapterContent, type ChapterContentHandle } from '../components/ChapterContent';
import { formatHHMMSS, parseTimestamp, slugify } from '@/shared/text';

interface Bundle {
  chapter: Chapter;
  sources: Source[];
  summary?: Summary;
  qa: QaMessage[];
  cardCount: number;
}

export function ChapterRoute({
  chapterId,
  highlightQuery,
}: {
  chapterId: string;
  // When the chapter is opened from the Search tab, the query to highlight +
  // scroll to once the content is rendered.
  highlightQuery?: string;
}) {
  const [bundle, setBundle] = useState<Bundle | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [toast, setToast] = useState<string | null>(null);
  const [tests, setTests] = useState<Test[]>([]);

  useEffect(() => {
    send({ type: 'LIST_TESTS' }).then((r) => setTests(r.tests));
  }, [refreshKey]);

  const contentBoxRef = useRef<HTMLDivElement>(null);
  const handleRef = useRef<ChapterContentHandle | null>(null);

  useEffect(() => {
    let cancelled = false;
    setBundle(null);
    setError(null);
    Promise.all([
      send({ type: 'GET_CHAPTER', chapterId }),
      send({ type: 'LIST_CHAPTERS' }),
    ])
      .then(([b, l]) => {
        if (cancelled) return;
        const counts = l.chapters.find((c) => c.id === chapterId);
        setBundle({
          chapter: b.chapter,
          sources: b.sources,
          summary: b.summary,
          qa: b.qa,
          cardCount: counts?.cardCount ?? 0,
        });
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      cancelled = true;
    };
  }, [chapterId, refreshKey]);

  // After the chapter renders, highlight + scroll to the search query (if we
  // arrived from the Search tab). rAF so layout is ready; the child's onReady
  // has already populated handleRef (child effects run before this parent one).
  useEffect(() => {
    if (!bundle || !highlightQuery) return;
    const id = requestAnimationFrame(() => {
      handleRef.current?.scrollToText(highlightQuery);
    });
    return () => cancelAnimationFrame(id);
  }, [bundle, highlightQuery]);

  function flash(msg: string) {
    setToast(msg);
    window.setTimeout(() => setToast(null), 1800);
  }

  async function moveToTest(testId: string) {
    if (!bundle || testId === bundle.chapter.test_id) return;
    await send({ type: 'UPDATE_CHAPTER', chapterId, patch: { test_id: testId } });
    flash(`Moved to "${tests.find((t) => t.id === testId)?.title ?? 'test'}"`);
    setRefreshKey((k) => k + 1);
  }

  async function onAnchorClick(anchor: string, kind: 'timestamp' | 'heading') {
    if (!handleRef.current) {
      flash('Content not ready yet — try again in a sec.');
      return;
    }
    if (kind === 'heading') {
      const ok = handleRef.current.scrollToHeading(slugify(anchor));
      if (ok) {
        flash('Jumped to section');
      } else {
        flash(`No heading "${anchor}" in chapter text. Capture the chapter text source if you haven't.`);
      }
      return;
    }

    // Timestamp anchor.
    const seconds = parseTimestamp(anchor);
    const scrolled = handleRef.current.scrollToTimestamp(seconds);

    // Also seek the LMS tab if it's open. Report the combined outcome so the
    // user knows whether the LMS player actually moved.
    const lmsSeek = await seekLmsTab(seconds, bundle?.chapter.source_url);

    if (scrolled && lmsSeek === 'seeked') {
      flash(`Seeked LMS + jumped to ${anchor}`);
    } else if (lmsSeek === 'seeked') {
      flash(`Seeked LMS to ${anchor}`);
    } else if (scrolled) {
      const reason =
        lmsSeek === 'no-tab'
          ? '(no LMS tab open)'
          : lmsSeek === 'no-url'
            ? ''
            : `· LMS: ${lmsSeek}`;
      flash(`Jumped to ${anchor} ${reason}`.trim());
    } else {
      flash('No transcript loaded — capture the video VTT in the Videos tab.');
    }
  }

  async function seekLmsTab(
    seconds: number,
    url: string | undefined
  ): Promise<'seeked' | 'no-url' | 'no-tab' | string> {
    if (!url) return 'no-url';
    // Webapp has no chrome.tabs and no LMS tab to seek — silently report
    // "no-tab" so the surrounding flash() shows a sensible message.
    if (typeof chrome === 'undefined' || typeof chrome.tabs === 'undefined') {
      return 'no-tab';
    }
    try {
      const tabs = await chrome.tabs.query({ url: `${new URL(url).origin}/*` });
      const tab = tabs.find((t) => t.url?.startsWith(url)) ?? tabs[0];
      if (tab?.id === undefined) return 'no-tab';
      const r = await send({ type: 'SEEK_VIDEO_IN_TAB', tabId: tab.id, seconds });
      if (r.ok) return 'seeked';
      return r.diag ?? `failed (frames=${r.framesTried}, withVideo=${r.framesWithVideo})`;
    } catch (e) {
      return e instanceof Error ? e.message : String(e);
    }
  }

  if (error) {
    return <div className="p-4 text-sm text-red-600">Error loading chapter: {error}</div>;
  }
  if (!bundle) {
    return <div className="p-4 text-sm text-slate-500">Loading chapter…</div>;
  }

  const hasContent = bundle.sources.some(
    (s) => (s.raw_markdown && s.raw_markdown.length > 50) || (s.vtt_cues && s.vtt_cues.length > 0)
  );

  return (
    <div className="space-y-4 p-4">
      <header className="flex items-start justify-between gap-2">
        <div>
          <h1 className="text-base font-semibold">{bundle.chapter.title}</h1>
          <p className="text-xs text-slate-500">
            {bundle.sources.length} source{bundle.sources.length === 1 ? '' : 's'} · {bundle.cardCount}{' '}
            card{bundle.cardCount === 1 ? '' : 's'}
          </p>
        </div>
        {tests.length > 1 && (
          <label className="flex items-center gap-1 text-[11px] text-slate-500">
            Test
            <select
              value={bundle.chapter.test_id}
              onChange={(e) => void moveToTest(e.target.value)}
              className="rounded border border-slate-300 px-1.5 py-0.5 text-xs text-slate-800"
              title="Move this chapter to another test"
            >
              {tests.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.title}
                </option>
              ))}
            </select>
          </label>
        )}
      </header>

      <SourcesList
        sources={bundle.sources}
        onChanged={() => setRefreshKey((k) => k + 1)}
      />

      <GenerateActions
        chapterId={chapterId}
        hasSummary={!!bundle.summary}
        cardCount={bundle.cardCount}
        hasContent={hasContent}
        onSummaryUpdated={() => setRefreshKey((k) => k + 1)}
        onCardsUpdated={() => setRefreshKey((k) => k + 1)}
      />

      <SummaryPanel summary={bundle.summary} onAnchorClick={onAnchorClick} />

      <ChapterContent
        sources={bundle.sources}
        containerRef={contentBoxRef}
        onReady={(h) => {
          handleRef.current = h;
        }}
        onTimestampClick={(seconds) => onAnchorClick(formatHHMMSS(seconds), 'timestamp')}
      />

      <QAChat chapterId={chapterId} initialHistory={bundle.qa} />

      {toast && (
        <div className="pointer-events-none fixed bottom-4 left-1/2 z-50 -translate-x-1/2 rounded-full bg-slate-900 px-3 py-1 text-xs text-white shadow-lg">
          {toast}
        </div>
      )}
    </div>
  );
}

